/**
 * The reactive contract the form engine is written against, and nothing else.
 *
 * The engine never imports a framework: it needs something that can create writable signals, derived
 * computations and effects. An adapter binds those to its host's own primitives so the engine's
 * state takes part in that host's change detection.
 *
 * The reference implementation lives next door, in `vanilla-reactivity.ts`. It used to live here,
 * which meant importing the interface pulled in four hundred lines of scheduler and its module-level
 * queue — a cost paid by every consumer that only wanted to name the type.
 *
 * `capabilities`, `createScope`, `id` and `kind` are optional for now so adapters keep compiling
 * unmodified; they become load-bearing as each is migrated.
 */
/**
 * Narrows to a reactivity that reports (and implements) real runtime batching.
 *
 * Used where a change is one statement made of several writes — a row's cells, a list's rows. A
 * runtime whose computations run eagerly observes each write on its own otherwise, and sees a form
 * that is half-written: a shape its own schema does not describe.
 */
export function reactivityBatches(rx) {
    return (rx.capabilities?.batching === true &&
        typeof rx.batch === "function");
}
/**
 * Whether this reactivity can run effects.
 *
 * `capabilities` is required, so in TypeScript this is just the field. It is asked through a
 * function because the engine is also called from JavaScript, and an adapter object assembled by
 * hand can still arrive without it — in which case the answer is "no effects", exactly what the
 * `canEffect` alias reported before it was removed. The engine then skips async validators, drafts
 * and history with a warning rather than throwing on a missing property.
 */
/**
 * What a reactivity owes the engine, and what a signal it makes owes in turn.
 *
 * An adapter implements `MdyReactivity`, and the compiler checks it — but an adapter written in
 * JavaScript, or handed across a bundle boundary, or assembled by spreading another one, is checked
 * by nothing. The first missing member then arrives from inside the engine: `hasDraft.asReadonly is
 * not a function`, thrown in a file the adapter's author has never opened, naming a local variable
 * that means nothing to them.
 *
 * The second half is the one a type would not have caught anyway. `asReadonly` is declared on
 * `MdyWritableSignal`, a different interface from the one being implemented, so an adapter can
 * satisfy `MdyReactivity` completely and still hand back signals the engine cannot use.
 */
/**
 * The members the engine cannot construct without, measured rather than assumed.
 *
 * `effect` is deliberately not here, and neither is `capabilities`: a reactivity that cannot run
 * reactions is a supported thing — the engine degrades and reports what it could not do through the
 * diagnostics sink — so refusing one here would turn a documented fallback into a crash. What is
 * listed is what the engine calls unconditionally while building a form.
 */
const REACTIVITY_MEMBERS = [
    ["signal", "make a writable signal"],
    ["computed", "derive a value from other signals"],
    ["untracked", "read a signal without depending on it"],
];
const WRITABLE_SIGNAL_MEMBERS = [
    ["set", "replace the value"],
    ["update", "derive the next value from the current one"],
    ["asReadonly", "hand out a view that cannot be written through"],
];
/**
 * What this reactivity is missing, in the words its author can act on. Empty when the engine can
 * use it.
 *
 * Reported rather than thrown, so the caller decides where the sentence appears: an engine says it
 * at construction, a test asserts on it.
 */
export function missingReactivityMembers(rx) {
    if (typeof rx !== "object" || rx === null) {
        return [`a reactivity must be an object, and this is ${rx === null ? "null" : typeof rx}`];
    }
    const held = rx;
    const missing = REACTIVITY_MEMBERS
        .filter(([member]) => typeof held[member] !== "function")
        .map(([member, purpose]) => `${member}() — ${purpose}, required by MdyReactivity`);
    if (missing.length > 0)
        return missing;
    // The signal is made rather than assumed: what `signal()` returns is the contract, and only
    // calling it can say whether the returned value keeps it.
    let made;
    try {
        made = held.signal(undefined);
    }
    catch (error) {
        return [`signal() threw when the engine called it: ${error instanceof Error ? error.message : String(error)}`];
    }
    if (typeof made !== "function") {
        return [`signal() returned ${typeof made}, and a signal is read by calling it`];
    }
    const signal = made;
    return WRITABLE_SIGNAL_MEMBERS
        .filter(([member]) => typeof signal[member] !== "function")
        .map(([member, purpose]) => `signal().${member}() — ${purpose}, required by MdyWritableSignal`);
}
export function reactivityRunsEffects(rx) {
    return rx.capabilities?.effects === true;
}
